// src/Tenant-Admin/TimeTable/BaseWeekTimetable.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useTimetable } from './hooks/useTimetable';
import { useAcademicData } from './hooks/useAcademicData';
import { useModal } from './hooks/useModal';
import TimetableHeader from './components/TimetableHeader';
import SelectionPanel from './components/SelectionPanel';
import EditTimetable from './components/EditTimetable';
import ViewTimetable from './components/ViewTimetable';
import DailyOverrideModal from './components/EditTimetable/DailyOverrideModal';
import ConflictApprovalModal from './components/ConflictApprovalModal';
import Modal from './components/common/Modal';
import ExcelBulkUpload from './components/EditTimetable/ExcelBulkUpload';
import PreviewModal from './components/common/PreviewModal';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { FaUsers, FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaTimes } from 'react-icons/fa';
import api from '../../api';

// ============================================================
// Custom Toast/Alert Component
// ============================================================
const ToastAlert = ({ isOpen, message, type, onClose, autoClose = true, duration = 4000 }) => {
  const [visible, setVisible] = useState(isOpen);

  useEffect(() => {
    setVisible(isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (visible && autoClose && type !== 'error') {
      const timer = setTimeout(() => {
        setVisible(false);
        if (onClose) onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [visible, autoClose, duration, type, onClose]);

  if (!visible) return null;

  const getIcon = () => {
    switch (type) {
      case 'success': return <FaCheckCircle className="text-green-500 text-xl" />;
      case 'error': return <FaExclamationTriangle className="text-red-500 text-xl" />;
      case 'warning': return <FaExclamationTriangle className="text-yellow-500 text-xl" />;
      default: return <FaInfoCircle className="text-blue-500 text-xl" />;
    }
  };

  const getColors = () => {
    switch (type) {
      case 'success': return 'bg-green-50 border-green-400 text-green-800';
      case 'error': return 'bg-red-50 border-red-400 text-red-800';
      case 'warning': return 'bg-yellow-50 border-yellow-400 text-yellow-800';
      default: return 'bg-blue-50 border-blue-400 text-blue-800';
    }
  };

  return (
    <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[10000] w-full max-w-md">
      <div className={`border-l-4 rounded-lg shadow-lg p-4 ${getColors()} animate-slide-down`}>
        <div className="flex items-start">
          <div className="flex-shrink-0 mr-3">
            {getIcon()}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium">{message}</p>
          </div>
          <button
            onClick={() => {
              setVisible(false);
              if (onClose) onClose();
            }}
            className="flex-shrink-0 ml-3 text-gray-400 hover:text-gray-600"
          >
            <FaTimes />
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Main Component
// ============================================================
export default function BaseWeekTimetable(props) {
  console.log('[BaseWeekTimetable] Component mounted');
  
  const location = useLocation();
  const { tenantId: paramTenantId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  // ✅ Get HOD-specific props
  const hodDepartmentId = props.hodDepartmentId || location.state?.hodDepartmentId || null;
  const isHODView = props.isHODView || false;
  const userRole = props.userRole || localStorage.getItem('userRole');

  const tenantId = paramTenantId || location.state?.tenantId || props.tenantId;

  const [mode, setMode] = useState('view');
  const [viewMode, setViewMode] = useState('base');
  const [baseMeta, setBaseMeta] = useState({});
  
  // Daily Override Modal State
  const [overrideModalVisible, setOverrideModalVisible] = useState(false);
  const [selectedDateForOverride, setSelectedDateForOverride] = useState(null);
  const [facultyData, setFacultyData] = useState([]);

  // Academic year details
  const [academicYearDetails, setAcademicYearDetails] = useState(null);

  // User info from token
  const [userId, setUserId] = useState(null);
  const [userName, setUserName] = useState('');
  const [organizationId, setOrganizationId] = useState('');

  // ============================================================
  // Toast Alert State
  // ============================================================
  const [toast, setToast] = useState({
    isOpen: false,
    message: '',
    type: 'info'
  });

  // ============================================================
  // Excel Upload States
  // ============================================================
  const [uploadedData, setUploadedData] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [isSavingBulk, setIsSavingBulk] = useState(false);
  
  // Excel Upload - Multi-Class Selection
  const [excelSelectedClassIds, setExcelSelectedClassIds] = useState([]);
  const [showClassSelector, setShowClassSelector] = useState(false);

  console.log('[BaseWeekTimetable] Initial state:', {
    tenantId,
    mode,
    viewMode,
    isHODView,
    hodDepartmentId,
    userRole
  });

  // ============================================================
  // Toast Helper
  // ============================================================
  const showToast = useCallback((message, type = 'info') => {
    setToast({ isOpen: true, message, type });
  }, []);

  const hideToast = useCallback(() => {
    setToast(prev => ({ ...prev, isOpen: false }));
  }, []);

  // ============================================================
  // Decode token
  // ============================================================
  useEffect(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        console.log('[BaseWeekTimetable] Token payload:', payload);
        setUserId(payload.userId || payload.authUserId || null);
        setUserName(payload.name || payload.email || '');
        setOrganizationId(payload.organizationId || payload.context?.id || '');
      } catch (e) {
        console.error('[BaseWeekTimetable] Error decoding token:', e);
      }
    }
  }, [token]);

  const {
    academicYears,
    selectedAcademicYear,
    setSelectedAcademicYear,
    isSemesterBased,
    semesters,
    selectedSemester,
    setSelectedSemester,
    classes: allClasses,
    selectedClassId,
    setSelectedClassId,
    terms,
    selectedTermId,
    setSelectedTermId,
    selectedTerm,
    fetchAcademicData
  } = useAcademicData(tenantId, token);

  // ✅ HOD FILTERING: Filter classes by department
  const [filteredClasses, setFilteredClasses] = useState([]);

  useEffect(() => {
    console.log('[BaseWeekTimetable] HOD Filtering:', { 
      isHODView, 
      hodDepartmentId, 
      allClassesCount: allClasses?.length 
    });

    if (isHODView && hodDepartmentId && allClasses.length > 0) {
      const filtered = allClasses.filter(classItem => {
        // Check if class belongs to HOD's department
        const classDeptId = classItem.departmentId?._id || classItem.departmentId || '';
        const matchesDept = String(classDeptId) === String(hodDepartmentId);
        
        // Also check departmentIds array
        let matchesDeptIds = false;
        if (classItem.departmentIds && Array.isArray(classItem.departmentIds)) {
          matchesDeptIds = classItem.departmentIds.some(id => 
            String(id) === String(hodDepartmentId)
          );
        }
        
        // Also check departmentSubjects
        let matchesDeptSubjects = false;
        if (classItem.departmentSubjects && Array.isArray(classItem.departmentSubjects)) {
          matchesDeptSubjects = classItem.departmentSubjects.some(deptSubj => 
            String(deptSubj.departmentId) === String(hodDepartmentId)
          );
        }
        
        return matchesDept || matchesDeptIds || matchesDeptSubjects;
      });
      
      console.log('[BaseWeekTimetable] Filtered classes:', filtered.length);
      setFilteredClasses(filtered);
    } else {
      setFilteredClasses(allClasses || []);
    }
  }, [allClasses, hodDepartmentId, isHODView]);

  // ✅ Use filteredClasses
  const classes = filteredClasses;

  // Fetch academic year details
  useEffect(() => {
    const fetchAcademicYearDetails = async () => {
      if (!selectedAcademicYear || !tenantId) return;
      
      try {
        console.log('[BaseWeekTimetable] Fetching academic year details for:', selectedAcademicYear);
        const response = await api.get(`/api/academic-years/${selectedAcademicYear}`, {
          params: { tenantId },
          headers: token ? { Authorization: `Bearer ${token}` } : undefined
        });
        
        if (response.data.success) {
          setAcademicYearDetails(response.data.data);
          console.log('[BaseWeekTimetable] Academic year details:', response.data.data);
        }
      } catch (err) {
        console.error('[BaseWeekTimetable] Error fetching academic year details:', err);
        setAcademicYearDetails(null);
      }
    };

    fetchAcademicYearDetails();
  }, [selectedAcademicYear, tenantId, token]);

  const {
    weekTemplate,
    setWeekTemplate,
    baseWeekDocId,
    subjects,
    labBatches,
    isLoading,
    loadingButton,
    error,
    saveStatus,
    pendingConflicts,
    showConflictModal,
    fetchBaseWeek,
    saveTimetable,
    deleteTimetable,
    deleteWeekTemplateEntry,
    handleSubjectChange,
    handleRemoveEntry,
    approveConflictsAndSave,
    rejectConflicts,
    setShowConflictModal
  } = useTimetable(
    selectedClassId, 
    selectedAcademicYear, 
    selectedSemester, 
    selectedTerm,
    isSemesterBased, 
    tenantId,
    token
  );

  const resetTimetableForm = useCallback(() => {
    console.log('[BaseWeekTimetable] Resetting timetable form');
    setWeekTemplate([]);
  }, [setWeekTemplate]);
 
  const { modalOpen, modalMessage, modalType, closeModal } = useModal();

  const [sessionExpired, setSessionExpired] = useState(false);
  const [countdown, setCountdown] = useState(10);

  const decodeJwtPayload = useCallback((jwt) => {
    try {
      const parts = jwt.split('.');
      if (parts.length < 2) return null;
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
      return JSON.parse(window.atob(padded));
    } catch (e) {
      console.error('[decodeJwtPayload] Error:', e);
      return null;
    }
  }, []);

  const triggerSessionExpired = useCallback((message = 'Session expired. Please login') => {
    console.log('[triggerSessionExpired]', message);
    showToast(message, 'error');
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    setSessionExpired(true);
    setCountdown(10);
  }, [showToast]);

  const handleApiAuthError = useCallback((err) => {
    const status = err?.response?.status;
    if (status === 401) {
      triggerSessionExpired('Session expired. Please login');
      return true;
    }
    return false;
  }, [triggerSessionExpired]);

  // Session expiry check
  useEffect(() => {
    if (!token) return;
    const payload = decodeJwtPayload(token);
    if (!payload) {
      triggerSessionExpired('Session expired. Please login');
      return;
    }
    if (payload.exp && Number(payload.exp) < Math.floor(Date.now() / 1000)) {
      triggerSessionExpired('Session expired. Please login');
    }
  }, [token, decodeJwtPayload, triggerSessionExpired]);

  // Countdown for session expiry
  useEffect(() => {
    if (!sessionExpired) return;
    if (countdown <= 0) {
      navigate('/');
      return;
    }
    const timer = setInterval(() => {
      setCountdown(c => c - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [sessionExpired, countdown, navigate]);

  // Fetch faculty data
  const fetchFacultyData = useCallback(async () => {
    if (!tenantId) return;
    console.log('[fetchFacultyData] Fetching faculty data');
    try {
      const response = await api.get('/api/faculty/list', {
        params: { tenantId },
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });
      if (response.data.success) {
        console.log('[fetchFacultyData] Received faculty data:', response.data.data?.length);
        setFacultyData(response.data.data || []);
      } else {
        setFacultyData([]);
      }
    } catch (err) {
      console.error('[fetchFacultyData] Error:', err);
      if (!handleApiAuthError(err)) {
        setFacultyData([]);
      }
    }
  }, [tenantId, token, handleApiAuthError]);

  // Initial data fetch
  useEffect(() => {
    const safeFetchAcademicData = async () => {
      try {
        await fetchAcademicData();
        await fetchFacultyData();
      } catch (err) {
        if (!handleApiAuthError(err)) {
          console.error('[safeFetchAcademicData] Error:', err);
        }
      }
    };
    safeFetchAcademicData();
  }, [fetchAcademicData, fetchFacultyData, handleApiAuthError]);

  // Daily Override Handlers
  const handleOpenOverrideModal = useCallback((date) => {
    console.log('[handleOpenOverrideModal] Opening for date:', date);
    if (!selectedClassId || !selectedAcademicYear) {
      showToast('Please select a class and academic year first', 'warning');
      return;
    }
    setSelectedDateForOverride(date);
    setOverrideModalVisible(true);
  }, [selectedClassId, selectedAcademicYear, showToast]);

  const handleOverrideSave = useCallback((overrideData) => {
    console.log('[handleOverrideSave] Override saved:', overrideData);
    showToast('Daily override saved successfully!', 'success');
    setOverrideModalVisible(false);
    if (mode === 'view') {
      fetchBaseWeek();
    }
  }, [showToast, mode, fetchBaseWeek]);

  const handleOverrideCancel = useCallback(() => {
    console.log('[handleOverrideCancel] Cancelling');
    setOverrideModalVisible(false);
    setSelectedDateForOverride(null);
  }, []);

  // ============================================================
  // Excel Upload Handlers
  // ============================================================
  const handleUploadComplete = useCallback((data) => {
    console.log('[BaseWeekTimetable] Upload complete:', data);
    console.log('[BaseWeekTimetable] Data.success:', data.success);
    console.log('[BaseWeekTimetable] Data.data length:', data.data?.length);
    
    setUploadedData(data);
    
    if (data.success && data.data && data.data.length > 0) {
      console.log('[BaseWeekTimetable] ✅ Opening preview modal');
      setShowPreviewModal(true);
    } else {
      console.log('[BaseWeekTimetable] ❌ Not opening preview - conditions not met');
      if (data.errors && data.errors.length > 0) {
        showToast(`Validation failed: ${data.errors.length} error(s) found`, 'error');
      } else {
        showToast('No valid data found in the uploaded file', 'warning');
      }
    }
  }, [showToast]);

  const handleConfirmBulkSave = useCallback(async () => {
    if (!uploadedData || !uploadedData.data) {
      showToast('No data to save', 'warning');
      return;
    }

    setIsSavingBulk(true);
    try {
      const payload = {
        tenantId: tenantId,
        academicYearId: selectedAcademicYear,
        semesterType: selectedSemester || null,
        termId: selectedTerm?._id || null,
        classes: uploadedData.data,
        options: {
          autoResolveConflicts: true,
          overwrite: true
        }
      };

      const response = await api.post('/api/timetable/bulk-save', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        showToast(
          `✅ Successfully saved ${response.data.summary?.savedClasses || 0} classes with ${response.data.summary?.totalSlots || 0} sessions`,
          'success'
        );
        setShowPreviewModal(false);
        setUploadedData(null);
        // Reset selected classes after successful save
        setExcelSelectedClassIds([]);
        if (mode === 'view' && selectedClassId) {
          fetchBaseWeek();
        }
      } else {
        showToast(response.data.message || 'Failed to save timetables', 'error');
      }
    } catch (error) {
      console.error('[BaseWeekTimetable] Bulk save error:', error);
      showToast(error.response?.data?.message || 'Failed to save timetables', 'error');
    } finally {
      setIsSavingBulk(false);
    }
  }, [uploadedData, tenantId, selectedAcademicYear, selectedSemester, selectedTerm, token, showToast, mode, selectedClassId, fetchBaseWeek]);

  const handleCancelPreview = useCallback(() => {
    setShowPreviewModal(false);
    setUploadedData(null);
  }, []);

  // ============================================================
  // Save Timetable Handler (with Toast)
  // ============================================================
  const handleSaveTimetable = useCallback(async (payload) => {
    try {
      const result = await saveTimetable(payload);
      if (result?.hasWarnings) {
        showToast(`⚠️ Timetable saved with ${result.warnings?.length || 0} conflict(s)`, 'warning');
      } else if (result?.success !== false) {
        showToast('✅ Timetable saved successfully!', 'success');
      }
      return result;
    } catch (error) {
      console.error('[BaseWeekTimetable] Save error:', error);
      showToast(error.response?.data?.message || 'Failed to save timetable', 'error');
      throw error;
    }
  }, [saveTimetable, showToast]);

  // ============================================================
  // Delete Timetable Handler (with Toast)
  // ============================================================
  const handleDeleteTimetable = useCallback(async () => {
    try {
      const result = await deleteTimetable();
      if (result?.success) {
        showToast('🗑️ Timetable deleted successfully', 'success');
      }
      return result;
    } catch (error) {
      console.error('[BaseWeekTimetable] Delete error:', error);
      showToast(error.response?.data?.message || 'Failed to delete timetable', 'error');
      throw error;
    }
  }, [deleteTimetable, showToast]);

  // ============================================================
  // Load base week metadata
  // ============================================================
  useEffect(() => {
    const loadBaseWeekAndMeta = async () => {
      if (!selectedClassId || mode !== 'view') return;
      
      console.log('[loadBaseWeekAndMeta] Loading base week and metadata');
      
      try {
        const resData = await fetchBaseWeek();
        console.log('[loadBaseWeekAndMeta] fetchBaseWeek result:', resData);
        
        const respMeta = resData?.meta || null;
        
        const classesList = (classes || []);
        const classObj = classesList.find(c => String(c._id || c.id || c.classId) === String(selectedClassId));
        const academicYearObj = (academicYears || []).find(y => String(y._id || y.id) === String(selectedAcademicYear));
        
        let termName = '';
        let termNumber = '';
        let semesterInfo = '';
        
        if (academicYearObj && academicYearObj.terms && Array.isArray(academicYearObj.terms)) {
          const foundTerm = academicYearObj.terms.find(t => String(t._id?.$oid || t._id || t.id) === String(selectedTerm?._id || selectedTermId));
          if (foundTerm) {
            termName = foundTerm.termLabel || foundTerm.termName || foundTerm.label || '';
            termNumber = foundTerm.termNumber || '';
            semesterInfo = termName;
          }
        } else if (academicYearObj && academicYearObj.semesters && Array.isArray(academicYearObj.semesters)) {
          const foundSemester = academicYearObj.semesters.find(s => String(s._id) === String(selectedSemester));
          if (foundSemester) {
            semesterInfo = foundSemester.semesterName;
            termName = foundSemester.semesterName;
          }
        }
        
        if (respMeta && Object.keys(respMeta).length > 0) {
          setBaseMeta({
            ...respMeta,
            className: respMeta.className || classObj?.name || classObj?.className || '',
            academicYearName: respMeta.academicYearName || academicYearObj?.name || academicYearObj?.academicYearName || '',
            academicYear: respMeta.academicYear || academicYearObj?.name || academicYearObj?.academicYearName || '',
            departmentName: respMeta.departmentName || classObj?.departmentName || classObj?.department?.name || '',
            section: respMeta.section || classObj?.sectionName || classObj?.section || '',
            isSemesterBased: respMeta.isSemesterBased !== undefined ? respMeta.isSemesterBased : (academicYearObj?.isSemesterBased || false),
            semester: respMeta.semester || respMeta.semesterName || respMeta.term || semesterInfo,
            semesterName: respMeta.semesterName || semesterInfo,
            term: respMeta.term || termName,
            termName: respMeta.termName || termName,
            termNumber: respMeta.termNumber || termNumber,
            classId: selectedClassId,
            academicYearId: selectedAcademicYear
          });
        } else {
          const fallbackMeta = {
            className: classObj?.name || classObj?.className || '',
            academicYearName: academicYearObj?.name || academicYearObj?.academicYearName || '',
            academicYear: academicYearObj?.name || academicYearObj?.academicYearName || '',
            departmentName: classObj?.departmentName || classObj?.department?.name || '',
            section: classObj?.sectionName || classObj?.section || '',
            tenantId: tenantId || '',
            term: termName,
            termName: termName,
            semester: semesterInfo,
            semesterName: semesterInfo,
            termNumber: termNumber,
            isSemesterBased: academicYearObj?.isSemesterBased || false,
            classId: selectedClassId,
            academicYearId: selectedAcademicYear
          };
          setBaseMeta(fallbackMeta);
        }
      } catch (err) {
        console.error('[loadBaseWeekAndMeta] Error:', err);
        if (!handleApiAuthError(err)) {
          // error handled
        }
      }
    };
    loadBaseWeekAndMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClassId, mode, fetchBaseWeek, selectedAcademicYear, classes, academicYears, tenantId, handleApiAuthError, selectedTerm, selectedTermId, selectedSemester]);

  const handleAcademicYearChange = useCallback((academicYearId) => {
    console.log('[handleAcademicYearChange]', academicYearId);
    setSelectedAcademicYear(academicYearId);
    setSelectedSemester('');
    setSelectedTermId('');
    setSelectedClassId('');
    setAcademicYearDetails(null);
    // Clear Excel selections when academic year changes
    setExcelSelectedClassIds([]);
  }, [setSelectedAcademicYear, setSelectedSemester, setSelectedTermId, setSelectedClassId]);

  const [electiveGroups, setElectiveGroups] = useState([]);
  const [loadingElectives, setLoadingElectives] = useState(false);

  const selectedClassObj = (classes || []).find(c => String(c._id || c.id || c.classId) === String(selectedClassId));
  const resolvedDepartmentId = selectedClassObj?.departmentId || null;
  const selectedAcademicYearObj = (academicYears || []).find(y => String(y._id || y.id) === String(selectedAcademicYear)) || null;

  const semesterName = selectedSemester ? 
    (semesters.find(s => String(s._id) === String(selectedSemester))?.semesterName || '') : '';

  const fetchElectiveGroups = useCallback(async () => {
    if (!tenantId || !selectedClassId) {
      setElectiveGroups([]);
      return;
    }
    console.log('[fetchElectiveGroups] Fetching for class:', selectedClassId);
    
    try {
      setLoadingElectives(true);
      const res = await api.get(`/api/elective-groups/by-class/${selectedClassId}`, {
        params: { tenantId },
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('[fetchElectiveGroups] Received:', res.data?.data?.length);
      setElectiveGroups(res.data?.data || []);
    } catch (err) {
      console.error('[fetchElectiveGroups] Error:', err);
      if (!handleApiAuthError(err)) {
        setElectiveGroups([]);
      }
    } finally {
      setLoadingElectives(false);
    }
  }, [tenantId, selectedClassId, token, handleApiAuthError]);

  useEffect(() => {
    fetchElectiveGroups();
  }, [fetchElectiveGroups]);

  // ============================================================
  // Render
  // ============================================================
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* ============================================================
          Toast Alert
          ============================================================ */}
      <ToastAlert
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />

      {/* ============================================================
          Modal (Legacy - kept for compatibility)
          ============================================================ */}
      {!sessionExpired && (
        <Modal
          isOpen={modalOpen}
          message={modalMessage}
          type={modalType}
          onClose={closeModal}
        />
      )}
      
      {/* Conflict Approval Modal */}
      <ConflictApprovalModal
        isOpen={showConflictModal}
        conflicts={pendingConflicts}
        onApprove={approveConflictsAndSave}
        onReject={rejectConflicts}
        onClose={() => setShowConflictModal(false)}
        loading={loadingButton === 'save'}
      />

      {/* Excel Upload - Class Selection Modal */}
      {showClassSelector && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                <FaUsers className="inline mr-2 text-blue-600" />
                Select Classes for Excel Upload
              </h3>
              <button
                onClick={() => setShowClassSelector(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-96">
              <div className="flex justify-between items-center mb-3">
                <p className="text-sm text-gray-500">
                  {excelSelectedClassIds.length} of {classes?.length || 0} selected
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const allIds = (classes || []).map(c => c._id || c.id).filter(Boolean);
                      setExcelSelectedClassIds(allIds);
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => setExcelSelectedClassIds([])}
                    className="text-xs text-red-600 hover:text-red-800"
                  >
                    Clear All
                  </button>
                </div>
              </div>
              
              <div className="space-y-1.5">
                {(classes || []).map((cls) => {
                  const classId = cls._id || cls.id;
                  const className = cls.name || cls.className;
                  const isChecked = excelSelectedClassIds.includes(classId);
                  
                  return (
                    <label key={classId} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${
                      isChecked ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-gray-50 border border-transparent'
                    }`}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setExcelSelectedClassIds([...excelSelectedClassIds, classId]);
                          } else {
                            setExcelSelectedClassIds(excelSelectedClassIds.filter(id => id !== classId));
                          }
                        }}
                        className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700">{className}</span>
                      {isChecked && (
                        <span className="ml-auto text-xs text-indigo-600">✓</span>
                      )}
                    </label>
                  );
                })}
              </div>
              
              {(!classes || classes.length === 0) && (
                <p className="text-center text-gray-400 py-8">No classes available for this academic year</p>
              )}
            </div>
            
            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowClassSelector(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowClassSelector(false);
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Confirm ({excelSelectedClassIds.length} classes)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Excel Upload Preview Modal */}
      <PreviewModal
        isOpen={showPreviewModal}
        onClose={handleCancelPreview}
        onConfirm={handleConfirmBulkSave}
        data={uploadedData}
        isLoading={isSavingBulk}
        title="📋 Preview Timetable Data"
      />
      
      {sessionExpired && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)'
        }}>
          <div style={{ background: '#fff', padding: 24, borderRadius: 12, width: 420, maxWidth: '90%' }}>
            <h2 style={{ margin: 0, marginBottom: 8, fontSize: 18 }}>Session Expired</h2>
            <p style={{ marginTop: 0, color: '#444' }}>Your session has expired. Please login again to continue.</p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
              <div style={{ color: '#666' }}>Auto redirect in <strong>{countdown}s</strong></div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => { localStorage.removeItem('token'); localStorage.removeItem('authToken'); navigate('/'); }}
                  style={{ padding: '8px 12px', borderRadius: 6, background: '#2563eb', color: '#fff', border: 'none' }}
                >
                  Login now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto bg-white rounded-xl shadow-md p-6">
        <TimetableHeader
          mode={mode}
          setMode={setMode}
          selectedClassId={selectedClassId}
          setError={showToast}
          token={token} 
        />

        <SelectionPanel
          academicYears={academicYears}
          selectedAcademicYear={selectedAcademicYear}
          onAcademicYearChange={handleAcademicYearChange}
          isSemesterBased={isSemesterBased}
          semesters={semesters}
          selectedSemester={selectedSemester}
          onSemesterChange={setSelectedSemester}
          terms={terms}
          selectedTermId={selectedTermId}
          onTermChange={setSelectedTermId}
          selectedTerm={selectedTerm}
          classes={classes}
          selectedClassId={selectedClassId}
          onClassChange={setSelectedClassId}
          onLoadTimetable={fetchBaseWeek}
          isLoading={isLoading}
          onResetTimetable={resetTimetableForm}
          error={error}
          saveStatus={saveStatus}
          token={token} 
        />

        {/* ============================================================
            Excel Bulk Upload Section
            ============================================================ */}
        {selectedAcademicYear && classes && classes.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center gap-4 mb-3">
              <button
                onClick={() => setShowClassSelector(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm"
              >
                <FaUsers />
                Select Classes for Excel
                {excelSelectedClassIds.length > 0 && (
                  <span className="bg-white text-blue-600 rounded-full px-2 py-0.5 text-xs font-bold">
                    {excelSelectedClassIds.length}
                  </span>
                )}
              </button>
              <span className="text-xs text-gray-400">
                {excelSelectedClassIds.length === 0 
                  ? 'Using current class only' 
                  : `${excelSelectedClassIds.length} classes selected for bulk upload`}
              </span>
            </div>

            <ExcelBulkUpload
              tenantId={tenantId}
              academicYearId={selectedAcademicYear}
              semesterType={selectedSemester || ''}
              termId={selectedTerm?._id || ''}
              selectedClassIds={excelSelectedClassIds.length > 0 ? excelSelectedClassIds : (selectedClassId ? [selectedClassId] : [])}
              onUploadComplete={handleUploadComplete}
            />
          </div>
        )}

        {mode === 'add' && (
          <EditTimetable
            weekTemplate={weekTemplate}
            setWeekTemplate={setWeekTemplate}
            subjects={subjects}
            labBatches={labBatches}
            selectedClassId={selectedClassId}
            className={selectedClassObj?.className || selectedClassObj?.name || ''}
            tenantId={tenantId}
            organizationId={organizationId}
            selectedDepartmentId={resolvedDepartmentId}
            selectedAcademicYearId={selectedAcademicYear}
            selectedAcademicYear={selectedAcademicYearObj}
            selectedSemesterId={selectedSemester}
            selectedSemesterName={semesterName}
            selectedTerm={selectedTerm}
            isSemesterBased={isSemesterBased}
            electiveGroups={electiveGroups}
            loadingElectives={loadingElectives}
            onSave={handleSaveTimetable}
            onSubjectChange={handleSubjectChange}
            loadingButton={loadingButton}
            showModal={showToast}
            deleteWeekTemplateEntry={deleteWeekTemplateEntry}
            onRemoveEntry={handleRemoveEntry}
            onOverrideClick={handleOpenOverrideModal}
            token={token}
            createdBy={userId}
            createdByName={userName}
          />
        )}

        {mode === 'view' && (
          <ViewTimetable
            viewMode={viewMode}
            setViewMode={setViewMode}
            weekTemplate={weekTemplate}
            baseWeekDocId={baseWeekDocId}
            baseMeta={baseMeta}
            subjects={subjects}
            selectedClassId={selectedClassId}
            selectedAcademicYear={selectedAcademicYear}
            selectedSemester={selectedSemester}
            selectedTerm={selectedTerm}
            isSemesterBased={isSemesterBased}
            academicYears={academicYears}
            semesters={semesters}
            terms={terms}
            tenantId={tenantId}
            electiveGroups={electiveGroups}
            loadingElectives={loadingElectives}
            deleteTimetable={handleDeleteTimetable}
            isLoading={isLoading}
            labBatches={labBatches}
            onEdit={() => setMode('add')}
            onDelete={handleDeleteTimetable}
            loadingButton={loadingButton}
            showModal={showToast}
            token={token}
            onOverrideClick={handleOpenOverrideModal}
            facultyData={facultyData}
          />
        )}

        <DailyOverrideModal
          visible={overrideModalVisible}
          onCancel={handleOverrideCancel}
          onSave={handleOverrideSave}
          selectedDate={selectedDateForOverride}
          classId={selectedClassId}
          className={selectedClassObj?.className || selectedClassObj?.name || ''}
          academicYearId={selectedAcademicYear}
          academicYearName={selectedAcademicYearObj?.name || ''}
          academicYearStartDate={academicYearDetails?.startDate}
          academicYearEndDate={academicYearDetails?.endDate}
          semesterType={selectedSemester}
          termId={selectedTerm?._id}
          termName={selectedTerm?.termLabel || selectedTerm?.termName}
          termNumber={selectedTerm?.termNumber}
          isSemesterBased={isSemesterBased}
          tenantId={tenantId}
          organizationId={organizationId}
          departmentId={resolvedDepartmentId}
          selectedDepartmentId={resolvedDepartmentId}
          electiveGroups={electiveGroups}
          loadingElectives={loadingElectives}
          subjects={subjects}
          labBatches={labBatches}
          facultyData={facultyData}
          token={token}
          createdBy={userId}
          createdByName={userName}
        />
      </div>
    </div>
  );
}